import { useMemo } from 'react';

interface FiltersShape {
  search?: string;
  status?: string | string[];
  advancedFilters?: Record<string, unknown>;
}

interface UseHasActiveFiltersOptions {
  /** Include search in active filter detection (default: true) */
  includeSearch?: boolean;
  /** Include status in active filter detection (default: false, since status often has default) */
  includeStatus?: boolean;
}

/**
 * Detects if any filters are currently active.
 * Centralizes the hasActiveFilters logic used across the app.
 *
 * @param filters - The filter state object containing search, status, and advancedFilters
 * @param options - Configuration options for which filters to consider
 * @returns boolean indicating if any filters are active
 *
 * @example
 * ```tsx
 * const hasActiveFilters = useHasActiveFilters(filters);
 *
 * // With options
 * const hasActiveFilters = useHasActiveFilters(filters, {
 *   includeSearch: true,
 *   includeStatus: false,
 * });
 * ```
 */
export function useHasActiveFilters(
  filters: FiltersShape,
  options: UseHasActiveFiltersOptions = {}
): boolean {
  const { includeSearch = true, includeStatus = false } = options;

  return useMemo(() => {
    const hasSearch = includeSearch && Boolean(filters.search?.trim());
    const hasStatus = includeStatus && Boolean(filters.status);
    const hasAdvancedFilters = filters.advancedFilters
      ? Object.values(filters.advancedFilters).some(value =>
          Array.isArray(value) ? value.length > 0 : Boolean(value)
        )
      : false;

    return hasSearch || hasStatus || hasAdvancedFilters;
  }, [filters, includeSearch, includeStatus]);
}
